import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Users, ListTodo, CalendarDays, Banknote, Plus, LogOut } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

/**
 * Persistent mobile bottom navigation bar — 1:1 an die native App angelehnt:
 *   [Dashboard] [Kunden] [Aufgaben]  (＋ FAB "Neue Akte")  [Termine] [Finanzen] [Abmelden]
 *
 * The center FAB shows the *primary action* of the current page (e.g. "Neue Akte"
 * on /dashboard, "Neuer Kunde" on /kunden, …).
 *
 * Hidden on routes with their own bottom UI (CaseEditor steps).
 */
const QUICK_NAV = [
  { to: "/dashboard",   label: "Dashboard", icon: Home,         testid: "mobile-bnav-dashboard" },
  { to: "/kunden",      label: "Kunden",    icon: Users,        testid: "mobile-bnav-kunden" },
  { to: "/aufgaben",    label: "Aufgaben",  icon: ListTodo,     testid: "mobile-bnav-aufgaben" },
  { to: "/termine",     label: "Termine",   icon: CalendarDays, testid: "mobile-bnav-termine" },
  { to: "/buchhaltung", label: "Finanzen",  icon: Banknote,     testid: "mobile-bnav-buchhaltung" },
];

// Routes that already have their own bottom UI — suppress this bar there.
const HIDE_ON_PREFIXES = ["/cases/", "/case/"];

export default function MobileBottomBar({ primaryAction, unseenCount = 0 }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const hidden = HIDE_ON_PREFIXES.some((p) => location.pathname.startsWith(p));
  if (hidden) return null;

  const fab = primaryAction;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      {/* Spacer so page content isn't covered by the fixed bar. */}
      <div className="lg:hidden h-20" aria-hidden />

      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-zinc-200 pb-[env(safe-area-inset-bottom)] shadow-[0_-4px_16px_rgba(0,0,0,0.04)]"
        data-testid="mobile-bottom-bar"
      >
        <div className="relative h-[68px] px-1">
          {/* FAB — absolutely centered, halb über der Bar (nativer App-Look) */}
          {fab && (
            <button
              onClick={fab.onClick}
              disabled={fab.disabled}
              title={fab.label}
              aria-label={fab.label}
              data-testid={fab.testid || "mobile-bnav-fab"}
              className="absolute left-1/2 -translate-x-1/2 -top-6 h-14 w-14 rounded-full shadow-[0_6px_18px_rgba(59,130,246,0.45)] border-4 border-white flex items-center justify-center transition-transform active:scale-95 brand-gradient-bg text-white z-10"
            >
              {fab.icon ? <fab.icon className="h-6 w-6" strokeWidth={2.4} /> : <Plus className="h-6 w-6" strokeWidth={2.4} />}
            </button>
          )}

          <div className="grid grid-cols-7 h-full items-end pb-1.5">
            {QUICK_NAV.slice(0, 3).map((item) => (
              <NavItem key={item.to} item={item} badge={item.to === "/dashboard" ? unseenCount : 0} />
            ))}
            {/* FAB-Spalte: Label unter dem schwebenden Button (wie native App) */}
            <div className="flex flex-col items-center justify-end pb-0.5">
              {fab?.label && (
                <span className="text-[10px] font-bold text-blue-600 whitespace-nowrap leading-tight">
                  {fab.label}
                </span>
              )}
            </div>
            {QUICK_NAV.slice(3).map((item) => <NavItem key={item.to} item={item} />)}
            <button
              onClick={handleLogout}
              data-testid="mobile-bnav-logout"
              aria-label="Abmelden"
              className="flex flex-col items-center justify-end gap-1 py-1 text-zinc-400 hover:text-zinc-600 active:text-zinc-700 transition-colors"
            >
              <LogOut className="h-[22px] w-[22px]" strokeWidth={1.8} />
              <span className="text-[10px] font-semibold tracking-tight leading-none">Abmelden</span>
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}

function NavItem({ item, badge = 0 }) {
  const location = useLocation();
  const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
  const Icon = item.icon;
  return (
    <Link
      to={item.to}
      data-testid={item.testid}
      className={cn(
        "flex flex-col items-center justify-end gap-1 py-1 transition-colors",
        active ? "text-blue-600" : "text-zinc-400 hover:text-zinc-600"
      )}
    >
      <span className="relative">
        <Icon className="h-[22px] w-[22px]" strokeWidth={active ? 2.3 : 1.8} />
        {badge > 0 && (
          <span
            data-testid="mobile-orders-badge"
            className="absolute -top-1.5 -right-2.5 min-w-[16px] h-[16px] px-0.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow-[0_0_0_2px_white]"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span className={cn("text-[10px] tracking-tight leading-none", active ? "font-bold" : "font-semibold")}>
        {item.label}
      </span>
    </Link>
  );
}
